import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ICatalogue } from "../../apis/useGetCatalogueData/types";
import GoToCartButton from "../../components/GoToCartButton/GoToCartButton";
import GrillSelectionModal from "../../components/GrillSelectionModal/GrillSelectionModal";
import I18nText from "../../components/I18nText/I18nText";
import {
	useCartActions,
	useCartContext,
} from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PATH } from "../../router/path";
import {
	addDatadogAction,
	buildLocationContext,
	startNewDatadogSession,
} from "../../services/datadog";
import CategoryList from "./CategoryList/CategoryList";
import "./ProductListPage.scss";
import ProductSection from "./ProductSection/ProductSection";
import { useCatalogueData } from "./hooks/useCatalogueData";
import { useListPageHandler } from "./hooks/useListPageHandler";

const ListPageLogo = "/hamburgrice.webp";

const ProductListPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [orderType, setOrderType] = React.useState<"takeout" | "dine-in">("dine-in");
	const { state: cart } = useCartContext((state) => state.cart);
	const { state: selectedGrillNumber } = useCartContext((state) => state.selectedGrillNumber);
	const { state: grillOptions } = useConfigContext((state) => state.config.grillNumber);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);
	const { SET_GRILL_NUMBER } = useCartActions();
	const hasGrillOptions = Array.isArray(grillOptions) && grillOptions.length > 0;

	// Track whether the customer had a prior grill selection at mount time
	// (returning customer with localStorage). useState lazy initializer
	// because useRef is mocked in tests to re-evaluate each render.
	const [wasUndefinedAtMount] = React.useState(() => selectedGrillNumber === undefined);
	const [userClosedModal, setUserClosedModal] = React.useState(false);
	const [chipOpen, setChipOpen] = React.useState(false);

	// Auto-default to grillOptions[0] on first visit. No setState here — modal
	// visibility is derived below — to avoid setState-in-render storms under
	// the jest useEffect mock. The selectedGrillNumber === undefined guard
	// short-circuits on subsequent renders once the dispatch lands.
	React.useEffect(() => {
		if (hasGrillOptions && selectedGrillNumber === undefined && grillOptions?.[0]) {
			SET_GRILL_NUMBER(grillOptions[0].id);
		}
	}, [hasGrillOptions, selectedGrillNumber, grillOptions, SET_GRILL_NUMBER]);

	// Show modal when:
	// - User explicitly clicked the chip, OR
	// - First visit (grill was undefined at mount) AND user hasn't dismissed yet
	const showGrillModal = hasGrillOptions && (chipOpen || (wasUndefinedAtMount && !userClosedModal));

	const selectedGrillLabel = useMemo(() => {
		if (!hasGrillOptions || !selectedGrillNumber) return null;

		return grillOptions?.find((o) => o.id === selectedGrillNumber)?.label ?? null;
	}, [hasGrillOptions, selectedGrillNumber, grillOptions]);

	const totalCartItems = useMemo(() => {
		return cart.reduce((total, item) => total + item.quantity, 0);
	}, [cart]);

	const cartTotalCents = useMemo(
		() => cart.reduce((total, item) => total + item.price * item.quantity, 0),
		[cart]
	);

	React.useEffect(() => {
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = "";
			document.documentElement.style.overflow = "";
		};
	}, []);

	const { categories, products, isLoading } = useCatalogueData();
	const {
		productListRef,
		categoryListRef,
		productSectionRefs,
		handleCategoryClick,
		activeCategoryId,
	} = useListPageHandler(categories);

	const previousCategoryIdRef = useRef<string | undefined>(undefined);
	const previousOrderTypeRef = useRef<"takeout" | "dine-in">(orderType);

	// Fire once on mount; empty deps keep this stable. productCount /
	// categoryCount may be 0 if catalogue is still loading, which is the
	// honest first-paint value for the dashboard.
	useEffect(() => {
		addDatadogAction("productlist_page_mount", {
			...buildLocationContext(location, stall),
			productCount: products?.length ?? 0,
			categoryCount: categories?.length ?? 0,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!activeCategoryId) return;
		if (previousCategoryIdRef.current === activeCategoryId) return;
		// Skip the initial assignment so we only emit on real switches.
		if (previousCategoryIdRef.current !== undefined) {
			const switchedCategory = categories?.find((c) => c.id === activeCategoryId);

			addDatadogAction("productlist_category_switch", {
				...buildLocationContext(location, stall),
				categoryId: activeCategoryId,
				categoryName: switchedCategory?.name,
			});
		}
		previousCategoryIdRef.current = activeCategoryId;
	}, [activeCategoryId, categories, location, stall]);

	// Exported via closure for future toggle wiring; current UI toggle is
	// commented out, so this handler does not fire today. Keeping the
	// instrumentation here means re-enabling the button only requires
	// passing onClick={_handleOrderTypeToggle}.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _handleOrderTypeToggle = useCallback(() => {
		const previousType = previousOrderTypeRef.current;
		const newType: "takeout" | "dine-in" = previousType === "takeout" ? "dine-in" : "takeout";

		addDatadogAction("productlist_order_type_toggle", {
			...buildLocationContext(location, stall),
			previousType,
			newType,
		});
		previousOrderTypeRef.current = newType;
		setOrderType(newType);
	}, [location, stall]);

	const categoriesWithProducts = useMemo(() => {
		if (!products || !categories) return [];

		return categories
			.map((category) => ({
				...category,
				products: products.filter(
					(product: ICatalogue) =>
						product.food_supplier_category?.brand_category_id?.toString() === category.id
				),
			}))
			.filter((category) => category.products.length > 0);
	}, [products, categories]);

	const handleStartOver = useCallback(() => {
		addDatadogAction("productlist_start_over", {
			...buildLocationContext(location, stall),
			cartItemCount: totalCartItems,
		});
		startNewDatadogSession();
		navigate(PATH.LANDING);
	}, [navigate, location, stall, totalCartItems]);

	const handleCheckout = useCallback(() => {
		addDatadogAction("productlist_checkout", {
			...buildLocationContext(location, stall),
			cartItemCount: totalCartItems,
			cartTotal: cartTotalCents,
		});
		navigate(PATH.CART);
	}, [navigate, location, stall, totalCartItems, cartTotalCents]);

	const handleGrillConfirm = useCallback(
		(grillId: string | null) => {
			SET_GRILL_NUMBER(grillId);
			setUserClosedModal(true);
			setChipOpen(false);
		},
		[SET_GRILL_NUMBER]
	);

	const handleGrillChipClick = useCallback(() => setChipOpen(true), []);
	const handleGrillModalClose = useCallback(() => {
		setChipOpen(false);
		setUserClosedModal(true);
	}, []);

	return (
		<div className="ProductListPage">
			{showGrillModal && hasGrillOptions && (
				<GrillSelectionModal
					grillOptions={grillOptions}
					initialSelected={selectedGrillNumber ?? grillOptions?.[0]?.id ?? null}
					onConfirm={handleGrillConfirm}
					onClose={handleGrillModalClose}
				/>
			)}
			<div className="TopHeader">
				<img src={ListPageLogo} alt="List Page Logo" className="ListPageLogo" draggable={false} />
				{selectedGrillLabel && (
					<button
						type="button"
						className="GrillChip"
						data-testid="GrillChip"
						onClick={handleGrillChipClick}
					>
						{selectedGrillLabel}
					</button>
				)}
			</div>

			<div className="MainLayout">
				<div className="LeftSidebar">
					{isLoading ? (
						<div className="LoadingState">{t("common.loading")}</div>
					) : (
						<CategoryList
							categories={categories}
							activeCategoryId={activeCategoryId}
							onCategoryClick={handleCategoryClick}
							listRef={categoryListRef}
						/>
					)}
					<div className="SidebarFooter">
						<div className="ExpandArrow">▼</div>
					</div>
				</div>

				<div className="RightContent">
					{/* <div className="OrderTypeSection">
						<div className="OrderTypeButtons">
							<button
								className="OrderTypeButton"
								onClick={() => setOrderType(orderType === "takeout" ? "dine-in" : "takeout")}
							>
								{orderType === "takeout" ? (
									<I18nText enText="Takeout" cnText="外帶" />
								) : (
									<I18nText enText="Dine-in" cnText="內用" />
								)}
							</button>
						</div> */}
					{/* </div> */}
					<div className="ProductsSection" ref={productListRef}>
						{isLoading ? (
							<div className="LoadingState">{t("productList.loadingProducts")}</div>
						) : (
							categoriesWithProducts.map((category) => (
								<ProductSection
									key={category.id}
									category={category}
									sectionRefs={productSectionRefs}
								/>
							))
						)}
					</div>

					{/* Bottom Controls */}
					<div className="BottomControls">
						<button className="StartOverButton" onClick={handleStartOver}>
							<img
								src="/Startover.png"
								alt="Start Over"
								className="StartOverIcon"
								draggable={false}
							/>
							<I18nText enText="Start Over" cnText="重新開始" />
						</button>
						{totalCartItems > 0 && (
							<GoToCartButton onClick={handleCheckout} cartCount={totalCartItems} />
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductListPage;
